/**
 * Circuit Breaker Pattern
 * Prevents infinite API calls when services are down
 */

class CircuitBreaker {
  constructor(name, options = {}) {
    this.name = name;
    this.failureThreshold = options.failureThreshold || 5;
    this.timeout = options.timeout || 60000; // 1 minute
    this.resetTimeout = options.resetTimeout || 30000; // 30 seconds
    
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failureCount = 0;
    this.lastFailureTime = null;
    this.nextAttempt = null;
  }

  async execute(operation) {
    if (this.state === 'OPEN') {
      if (Date.now() < this.nextAttempt) {
        throw new Error(`Circuit breaker ${this.name} is OPEN. Next attempt in ${Math.ceil((this.nextAttempt - Date.now()) / 1000)}s`);
      }
      this.state = 'HALF_OPEN';
    }

    try {
      const result = await Promise.race([
        operation(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Operation timeout')), this.timeout)
        )
      ]);
      
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  onSuccess() {
    this.failureCount = 0;
    this.state = 'CLOSED';
  }

  onFailure() {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    
    if (this.failureCount >= this.failureThreshold) {
      this.state = 'OPEN';
      this.nextAttempt = Date.now() + this.resetTimeout;
      console.warn(`🔴 Circuit breaker ${this.name} is now OPEN (${this.failureCount} failures)`);
    }
  }

  getState() {
    return {
      name: this.name,
      state: this.state,
      failureCount: this.failureCount,
      nextAttempt: this.nextAttempt ? Math.ceil((this.nextAttempt - Date.now()) / 1000) : null
    };
  }
}

// Create circuit breakers for each API
export const circuitBreakers = {
  morpho: new CircuitBreaker('Morpho', { failureThreshold: 3, timeout: 10000 }),
  coingecko: new CircuitBreaker('CoinGecko', { failureThreshold: 3, timeout: 10000 }),
  etherscan: new CircuitBreaker('Etherscan', { failureThreshold: 3, timeout: 10000 }),
  coinglass: new CircuitBreaker('CoinGlass', { failureThreshold: 3, timeout: 10000 }),
  defillama: new CircuitBreaker('DefiLlama', { failureThreshold: 3, timeout: 10000 })
};

/**
 * Get all circuit breaker states
 */
export function getCircuitBreakerStates() {
  return Object.values(circuitBreakers).map(breaker => breaker.getState());
}

/**
 * Reset all circuit breakers
 */
export function resetAllCircuitBreakers() {
  Object.values(circuitBreakers).forEach(breaker => {
    breaker.state = 'CLOSED';
    breaker.failureCount = 0;
    breaker.nextAttempt = null;
  });
}
